import { ReactNode, createContext, useContext, useMemo } from 'react';
import { ApiError } from 'src/api/api';
import { API_PATH_PREFIX } from 'src/api/constants';
import { Enhet } from 'src/models/enhet';
import useSWR from 'swr';

type EnheterContext = {
  enheter: Enhet[];
  fylker: Enhet[];
  fylkerMap: Record<string, Enhet>;
  isLoadingEnheter: boolean;
};

export const EnheterContext = createContext<EnheterContext>(
  null as unknown as EnheterContext
);

export const useEnheter = () => {
  const context = useContext(EnheterContext);

  if (!context) {
    throw new Error('useEnheter must be used within ProvideEnheter');
  }

  return context;
};

export const ProvideEnheter = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useSWR<Enhet[], ApiError>(
    `${API_PATH_PREFIX}/public/enheter`
  );

  const enheter = useMemo(() => data ?? [], [data]);
  const fylker = useMemo(
    () => enheter.filter(x => x.type === 'Fylke'),
    [enheter]
  );

  const fylkerMap = useMemo(
    () =>
      fylker.reduce(
        (acc, enhet) => {
          acc[enhet.nummer] = enhet;
          return acc;
        },
        {} as Record<string, Enhet>
      ),
    [fylker]
  );

  return (
    <EnheterContext.Provider
      value={{
        enheter: enheter,
        fylker: fylker,
        fylkerMap: fylkerMap,
        isLoadingEnheter: isLoading,
      }}
    >
      {children}
    </EnheterContext.Provider>
  );
};
